<template>
  <v-container fluid class="pa-4">
    <!-- Encabezado -->
    <v-row class="mb-2">
      <v-col cols="12" class="d-flex align-center justify-space-between">
        <div>
          <h2 class="text-h5 font-weight-bold primary--text">
            <v-icon color="primary" class="mr-2">mdi-cash-register</v-icon>
            Conciliación de Ingresos en Efectivo y Cierres de Caja
          </h2>
          <span class="grey--text text--darken-1 subtitle-2">
            Verificación de montos recaudados en efectivo vs. depósitos bancarios de custodia
          </span>
        </div>
        <v-btn color="primary" outlined @click="cargarDatos">
          <v-icon left>mdi-refresh</v-icon> Actualizar
        </v-btn>
      </v-col>
    </v-row>

    <!-- Tarjetas de Alerta y Resumen General -->
    <v-row class="mb-4">
      <v-col cols="12" sm="4">
        <v-card outlined class="pa-3 border-left-blue">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="caption grey--text font-weight-bold">TOTAL EFECTIVO RECAUDADO</div>
              <div class="text-h6 font-weight-bold blue--text text--darken-2">
                Bs {{ totalEfectivoRecaudado.toFixed(2) }}
              </div>
            </div>
            <v-avatar color="blue lighten-5" size="44">
              <v-icon color="blue">mdi-cash-multiple</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="4">
        <v-card outlined class="pa-3 border-left-green">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="caption grey--text font-weight-bold">DEPOSITADO EN BANCO</div>
              <div class="text-h6 font-weight-bold green--text text--darken-2">
                Bs {{ totalDepositadoBanco.toFixed(2) }}
              </div>
            </div>
            <v-avatar color="green lighten-5" size="44">
              <v-icon color="green">mdi-bank-check</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="4">
        <v-card outlined class="pa-3" :class="diferenciaTotal !== 0 ? 'border-left-red' : 'border-left-grey'">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="caption grey--text font-weight-bold">DIFERENCIA / PENDIENTE DEPOSITAR</div>
              <div class="text-h6 font-weight-bold" :class="diferenciaTotal !== 0 ? 'red--text' : 'grey--text'">
                Bs {{ diferenciaTotal.toFixed(2) }}
              </div>
            </div>
            <v-avatar :color="diferenciaTotal !== 0 ? 'red lighten-5' : 'grey lighten-4'" size="44">
              <v-icon :color="diferenciaTotal !== 0 ? 'red' : 'grey'">
                {{ diferenciaTotal !== 0 ? 'mdi-alert-circle' : 'mdi-check-circle-outline' }}
              </v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Alertas Activas de Cajas no cerradas / vencidas -->
    <v-alert
      v-if="cajasFueraDePlazo.length > 0"
      type="warning"
      border="left"
      colored-border
      elevation="2"
      class="mb-4"
    >
      <div class="font-weight-bold subtitle-1 mb-1">
        <v-icon color="warning" class="mr-1">mdi-clock-alert</v-icon>
        Atención: Cajas Abiertas Fuera del Plazo Establecido ({{ cajasFueraDePlazo.length }})
      </div>
      <div class="caption mb-2">
        Las siguientes cajas permanecen abiertas superando el tiempo permitido de rendición:
      </div>
      <v-chip
        v-for="caja in cajasFueraDePlazo"
        :key="caja.id"
        class="mr-2 mb-1"
        color="orange lighten-4"
        outlined
        small
      >
        <strong>Caja #{{ caja.id }}</strong> - Sede: {{ caja.sede }} | Cajero: {{ caja.cajero }} | Días abierta: {{ caja.dias_abierta }} días
      </v-chip>
    </v-alert>

    <!-- Tabla Principal de Arqueo y Conciliación por Cajas -->
    <v-card outlined class="rounded-lg">
      <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3 d-flex justify-space-between">
        <span>
          <v-icon color="primary" class="mr-1">mdi-table-search</v-icon>
          Arqueo de Cajas y Depósitos Registrados
        </span>
        <v-row dense style="max-width: 450px" align="center">
          <v-col cols="6">
            <v-select
              v-model="filtroSede"
              :items="opcionesSedes"
              label="Filtrar por Sede"
              outlined
              dense
              hide-details
              clearable
              @change="filtrarTabla"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="busquedaTexto"
              label="Buscar por cajero/caja"
              append-icon="mdi-magnify"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
        </v-row>
      </v-card-title>

      <v-data-table
        :headers="headersCajas"
        :items="cajasFiltradas"
        :search="busquedaTexto"
        :loading="cargando"
        dense
        class="elevation-0"
      >
        <template v-slot:[`item.monto_efectivo`]="{ item }">
          <span class="font-weight-bold">Bs {{ Number(item.monto_efectivo).toFixed(2) }}</span>
        </template>
        <template v-slot:[`item.monto_depositado`]="{ item }">
          <span class="green--text font-weight-bold">Bs {{ Number(item.monto_depositado).toFixed(2) }}</span>
        </template>
        <template v-slot:[`item.diferencia`]="{ item }">
          <v-chip
            small
            :color="item.diferencia === 0 ? 'success' : 'error'"
            dark
            class="font-weight-medium"
          >
            Bs {{ Number(item.diferencia).toFixed(2) }}
          </v-chip>
        </template>
        <template v-slot:[`item.estado_caja`]="{ item }">
          <v-chip
            small
            :color="item.estado_caja === 'CERRADA' ? 'blue' : 'warning'"
            dark
          >
            {{ item.estado_caja }}
          </v-chip>
        </template>
        <template v-slot:[`item.acciones`]="{ item }">
          <v-btn
            x-small
            color="primary"
            outlined
            @click="verDetalleCaja(item)"
          >
            <v-icon left x-small>mdi-eye</v-icon> Ver Depósitos
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Diálogo de Detalle Depósitos de Caja -->
    <v-dialog v-model="modalDetalle" max-width="650">
      <v-card v-if="cajaSeleccionada">
        <v-card-title class="subtitle-1 font-weight-bold primary white--text py-2">
          Detalle Depósitos Bancarios - Caja #{{ cajaSeleccionada.id }} ({{ cajaSeleccionada.sede }})
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="mb-3">
            <strong>Cajero:</strong> {{ cajaSeleccionada.cajero }} |
            <strong>Fecha Apertura:</strong> {{ cajaSeleccionada.fecha_apertura }}
          </div>
          <v-divider class="mb-3" />
          <div class="subtitle-2 font-weight-bold mb-2">Comprobantes / Papeletas de Depósito en Sede:</div>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Nro Papeleta / Ref</th>
                  <th>Banco</th>
                  <th>Fecha Depósito</th>
                  <th>Monto (Bs)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(dep, i) in cajaSeleccionada.depositos" :key="i">
                  <td>{{ dep.referencia || dep.nro_comprobante }}</td>
                  <td>{{ dep.banco }}</td>
                  <td>{{ dep.fecha_deposito }}</td>
                  <td class="font-weight-bold green--text">Bs {{ Number(dep.monto).toFixed(2) }}</td>
                </tr>
                <tr v-if="!cajaSeleccionada.depositos || cajaSeleccionada.depositos.length === 0">
                  <td colspan="4" class="text-center grey--text">Sin comprobantes de depósito registrados</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn color="primary" text @click="modalDetalle = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import conciliacionService from './service/conciliacion.service';

export default {
  name: 'ConciliacionEfectivo',
  data() {
    return {
      cargando: false,
      filtroSede: null,
      busquedaTexto: '',

      cajas: [],
      cajasFiltradas: [],
      cajasFueraDePlazo: [],
      opcionesSedes: [],

      modalDetalle: false,
      cajaSeleccionada: null,

      headersCajas: [
        { text: 'Nro Caja', value: 'id' },
        { text: 'Sede', value: 'sede' },
        { text: 'Cajero / Responsable', value: 'cajero' },
        { text: 'Fecha Apertura', value: 'fecha_apertura' },
        { text: 'Recaudado Efectivo', value: 'monto_efectivo' },
        { text: 'Depositado Banco', value: 'monto_depositado' },
        { text: 'Diferencia', value: 'diferencia' },
        { text: 'Estado Caja', value: 'estado_caja' },
        { text: 'Acciones', value: 'acciones', sortable: false },
      ],
    };
  },
  computed: {
    userId() {
      return this.$store.state.DATA_USER?.id || 1;
    },
    totalEfectivoRecaudado() {
      return this.cajasFiltradas.reduce((acc, curr) => acc + (Number(curr.monto_efectivo) || 0), 0);
    },
    totalDepositadoBanco() {
      return this.cajasFiltradas.reduce((acc, curr) => acc + (Number(curr.monto_depositado) || 0), 0);
    },
    diferenciaTotal() {
      return this.totalEfectivoRecaudado - this.totalDepositadoBanco;
    },
  },
  mounted() {
    this.cargarDatos();
  },
  methods: {
    async cargarDatos() {
      this.cargando = true;
      try {
        const resp = await conciliacionService.obtenerConciliacionEfectivo({}, this.userId);
        const data = resp.data || {};
        this.cajas = data.cajas || [];
        this.cajasFueraDePlazo = data.cajas_vencidas || [];
        
        // Obtener sedes únicas para el filtro
        const sedesSet = new Set(this.cajas.map(c => c.sede).filter(Boolean));
        this.opcionesSedes = Array.from(sedesSet);

        this.filtrarTabla();
      } catch (err) {
        window.console.error('Error al cargar conciliación de efectivo:', err);
        // Fallback de demostración si la API aún no devuelve estructura
        this.cargarDemostracion();
      } finally {
        this.cargando = false;
      }
    },
    filtrarTabla() {
      if (!this.filtroSede) {
        this.cajasFiltradas = this.cajas;
      } else {
        this.cajasFiltradas = this.cajas.filter(c => c.sede === this.filtroSede);
      }
    },
    verDetalleCaja(item) {
      this.cajaSeleccionada = item;
      this.modalDetalle = true;
    },
    cargarDemostracion() {
      this.cajas = [
        {
          id: 1042,
          sede: 'La Paz - Central',
          cajero: 'Juan Pérez',
          fecha_apertura: '2026-07-20',
          monto_efectivo: 3500.00,
          monto_depositado: 3500.00,
          diferencia: 0.00,
          estado_caja: 'CERRADA',
          depositos: [
            { nro_comprobante: 'DEP-88412', banco: 'BISA', fecha_deposito: '2026-07-21', monto: 3500.00 },
          ],
        },
        {
          id: 1043,
          sede: 'Cochabamba',
          cajero: 'María Delgado',
          fecha_apertura: '2026-07-21',
          monto_efectivo: 2100.00,
          monto_depositado: 2000.00,
          diferencia: 100.00,
          estado_caja: 'ABIERTA',
          depositos: [
            { nro_comprobante: 'DEP-99120', banco: 'BNB', fecha_deposito: '2026-07-22', monto: 2000.00 },
          ],
        },
      ];
      this.cajasFueraDePlazo = [
        { id: 1043, sede: 'Cochabamba', cajero: 'María Delgado', dias_abierta: 3 },
      ];
      const sedesSet = new Set(this.cajas.map(c => c.sede));
      this.opcionesSedes = Array.from(sedesSet);
      this.filtrarTabla();
    },
  },
};
</script>

<style scoped>
.border-left-blue {
  border-left: 4px solid #1976d2 !important;
}
.border-left-green {
  border-left: 4px solid #4caf50 !important;
}
.border-left-red {
  border-left: 4px solid #f44336 !important;
}
.border-left-grey {
  border-left: 4px solid #9e9e9e !important;
}
</style>
